import React, { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Input,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { FiCheckCircle, FiCpu, FiMenu, FiRefreshCw, FiWifi, FiWifiOff, FiX } from 'react-icons/fi';
import SaktiSidebar from '../components/SaktiSidebar';
import SaktiRemoteSchemaTree from '../components/SaktiRemoteSchemaTree';
import SaktiRemoteQueryConsole from '../components/SaktiRemoteQueryConsole';
import SaktiRemoteDataGrid from '../components/SaktiRemoteDataGrid';
import SaktiRemoteSessionList from '../components/SaktiRemoteSessionList';
import SaktiRemoteSessionLogModal from '../components/SaktiRemoteSessionLogModal';
import { useSaktiRemoteInspection } from '../hooks/useSaktiRemoteInspection';
import { SAKTI_UI_SIZE } from '../uiSizes';
import { RemoteWSStatus } from '../types';

const WS_STATUS_COLOR: Record<RemoteWSStatus, string> = {
  idle: 'gray',
  connecting: 'yellow',
  connected: 'green',
  error: 'red',
  closed: 'gray',
};

const WS_STATUS_LABEL: Record<RemoteWSStatus, string> = {
  idle: 'Belum terhubung',
  connecting: 'Menghubungkan…',
  connected: 'Terhubung',
  error: 'Koneksi error',
  closed: 'Terputus',
};

const SaktiRemoteInspectionPage: React.FC = () => {
  const {
    wsStatus, activeSession, pendingApproval, sessionActive, schema, queryResult, queryLoading,
    sessions, sessionsTotal, sessionsLoading,
    sessionLogs, sessionLogsLoading,
    createSession, cancelSession, completeSession, connectWS, disconnectWS,
    approveClient, rejectClient,
    sendSchemaRequest, sendQuery, fetchSessions, fetchSessionLogs,
  } = useSaktiRemoteInspection();

  const { isOpen: isMobileSidebarOpen, onOpen: onMobileSidebarOpen, onClose: onMobileSidebarClose } = useDisclosure();
  const { isOpen: isLogModalOpen, onOpen: onLogModalOpen, onClose: onLogModalClose } = useDisclosure();

  const [nipInput, setNipInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [activeLogSessionId, setActiveLogSessionId] = useState<string | null>(null);

  const handleCreateSession = async () => {
    setCreating(true);
    const result = await createSession({ nip: nipInput || undefined });
    if (result) {
      connectWS(result.session_id);
    }
    setCreating(false);
  };

  const handleCancel = async () => {
    if (activeSession) {
      await cancelSession(activeSession.session_id);
      fetchSessions(sessionsPage);
    }
  };

  const handleComplete = async () => {
    if (activeSession) {
      await completeSession(activeSession.session_id);
      fetchSessions(sessionsPage);
    }
  };

  const handleViewLogs = async (sessionId: string) => {
    setActiveLogSessionId(sessionId);
    await fetchSessionLogs(sessionId);
    onLogModalOpen();
  };

  const handlePageChange = (page: number) => {
    setSessionsPage(page);
    fetchSessions(page);
  };

  return (
    <Box minH="100vh" bg="#ECEEF1">
      <Flex align="stretch">
        <Box display={{ base: 'none', xl: 'block' }}>
          <SaktiSidebar activeMenu="remote-inspection" />
        </Box>

        <Drawer isOpen={isMobileSidebarOpen} placement="left" onClose={onMobileSidebarClose} size="xs">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton zIndex={10} color="gray.500" />
            <DrawerBody p={0}>
              <SaktiSidebar activeMenu="remote-inspection" />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        <Box flex={1} minW={0} overflow="hidden" p={SAKTI_UI_SIZE.pagePadding}>
          <VStack align="stretch" spacing={SAKTI_UI_SIZE.sectionGap}>

            {/* Page header */}
            <HStack justify="space-between" align={{ base: 'start', md: 'center' }} flexWrap="wrap">
              <HStack spacing={3} align="center">
                <IconButton
                  aria-label="Open menu"
                  icon={<FiMenu />}
                  variant="outline"
                  bg="white"
                  display={{ base: 'flex', xl: 'none' }}
                  onClick={onMobileSidebarOpen}
                />
                <VStack align="start" spacing={0}>
                  <Text fontSize={SAKTI_UI_SIZE.pageTitle} fontWeight="bold" color="gray.900" lineHeight="shorter">
                    Remote Inspection
                  </Text>
                  <Text color="gray.600" fontSize={SAKTI_UI_SIZE.pageSubtitle}>
                    Inspeksi database Sakti secara real-time
                  </Text>
                </VStack>
              </HStack>
              <Badge colorScheme={WS_STATUS_COLOR[wsStatus]} fontSize="sm" px={3} py={1} borderRadius="full">
                {wsStatus === 'connected'
                  ? <FiWifi style={{ display: 'inline', marginRight: 4 }} />
                  : <FiWifiOff style={{ display: 'inline', marginRight: 4 }} />}
                {WS_STATUS_LABEL[wsStatus]}
              </Badge>
            </HStack>

            {/* Session setup */}
            <Box bg="white" borderRadius={SAKTI_UI_SIZE.cardRadius} p={SAKTI_UI_SIZE.cardPadding} shadow="sm">
              <Text fontWeight="semibold" fontSize="md" mb={3} color="gray.800">Buat Sesi Inspeksi</Text>
              {!activeSession ? (
                <HStack spacing={3} flexWrap="wrap">
                  <Input
                    placeholder="NIP karyawan (opsional)"
                    value={nipInput}
                    onChange={(e) => setNipInput(e.target.value)}
                    maxW="280px"
                    size="sm"
                    bg="gray.50"
                  />
                  <Button leftIcon={<FiCpu />} colorScheme="green" size="sm" isLoading={creating} onClick={handleCreateSession}>
                    Buat Sesi &amp; Dapatkan OTP
                  </Button>
                </HStack>
              ) : (
                <HStack spacing={6} flexWrap="wrap" align="center">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500">OTP untuk perangkat</Text>
                    <Text fontSize="3xl" fontWeight="bold" fontFamily="mono" color="green.600" letterSpacing="widest">
                      {activeSession.otp_code}
                    </Text>
                    <Text fontSize="xs" color="gray.400">
                      Berlaku hingga {new Date(activeSession.expires_at).toLocaleTimeString('id-ID')}
                    </Text>
                  </VStack>
                  <HStack spacing={2}>
                    {sessionActive ? (
                      <Button leftIcon={<FiCheckCircle />} colorScheme="blue" size="sm" onClick={handleComplete}>
                        Selesaikan Sesi
                      </Button>
                    ) : (
                      <Button leftIcon={<FiX />} colorScheme="red" variant="outline" size="sm" onClick={handleCancel}>
                        Batalkan Sesi
                      </Button>
                    )}
                  </HStack>
                </HStack>
              )}
            </Box>

            {/* Approval banner — shown when an Android client is waiting for admin approval */}
            {pendingApproval && (
              <Alert status="info" borderRadius={SAKTI_UI_SIZE.cardRadius} alignItems="flex-start" shadow="sm">
                <AlertIcon mt={1} />
                <Box flex="1">
                  <AlertTitle fontSize="md">Perangkat meminta koneksi</AlertTitle>
                  <AlertDescription fontSize="sm">
                    <Text><strong>Model:</strong> {pendingApproval.device_model}</Text>
                    <Text><strong>Versi App:</strong> {pendingApproval.app_version}</Text>
                    <Text><strong>Device ID:</strong> {pendingApproval.device_id}</Text>
                  </AlertDescription>
                </Box>
                <HStack ml={4} spacing={2} alignSelf="center" flexShrink={0}>
                  <Button size="sm" colorScheme="green" onClick={approveClient}>Setujui</Button>
                  <Button size="sm" colorScheme="red" variant="outline" onClick={rejectClient}>Tolak</Button>
                </HStack>
              </Alert>
            )}

            {/* Schema + Query + Results */}
            <Flex gap={4} align="stretch" direction={{ base: 'column', lg: 'row' }}>
              <Box
                bg="white"
                borderRadius={SAKTI_UI_SIZE.cardRadius}
                p={SAKTI_UI_SIZE.cardPadding}
                shadow="sm"
                w={{ base: 'full', lg: '280px' }}
                flexShrink={0}
                maxH="540px"
                overflowY="auto"
              >
                <HStack justify="space-between" mb={3}>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.800">Schema</Text>
                  <IconButton
                    aria-label="Muat schema"
                    icon={<FiRefreshCw />}
                    size="xs"
                    variant="ghost"
                    isDisabled={wsStatus !== 'connected'}
                    onClick={sendSchemaRequest}
                  />
                </HStack>
                <SaktiRemoteSchemaTree
                  schema={schema}
                  onSelectTable={(table) => sendQuery(`SELECT * FROM ${table} LIMIT 50`)}
                />
              </Box>

              <VStack flex={1} minW={0} align="stretch" spacing={4}>
                <Box bg="white" borderRadius={SAKTI_UI_SIZE.cardRadius} p={SAKTI_UI_SIZE.cardPadding} shadow="sm">
                  <Text fontWeight="semibold" fontSize="sm" color="gray.800" mb={3}>Query Console</Text>
                  <SaktiRemoteQueryConsole onRunQuery={sendQuery} loading={queryLoading} wsStatus={wsStatus} sessionActive={sessionActive} />
                </Box>
                <Box bg="white" borderRadius={SAKTI_UI_SIZE.cardRadius} p={SAKTI_UI_SIZE.cardPadding} shadow="sm">
                  <Text fontWeight="semibold" fontSize="sm" color="gray.800" mb={3}>Hasil Query</Text>
                  <SaktiRemoteDataGrid result={queryResult} loading={queryLoading} />
                </Box>
              </VStack>
            </Flex>

            <Divider />

            {/* Session history */}
            <Box bg="white" borderRadius={SAKTI_UI_SIZE.cardRadius} p={SAKTI_UI_SIZE.cardPadding} shadow="sm">
              <HStack justify="space-between" mb={3}>
                <Text fontWeight="semibold" fontSize="md" color="gray.800">Riwayat Sesi</Text>
                <IconButton
                  aria-label="Refresh"
                  icon={<FiRefreshCw />}
                  size="xs"
                  variant="ghost"
                  onClick={() => fetchSessions(sessionsPage)}
                />
              </HStack>
              <SaktiRemoteSessionList
                sessions={sessions}
                loading={sessionsLoading}
                total={sessionsTotal}
                page={sessionsPage}
                onViewLogs={handleViewLogs}
                onPageChange={handlePageChange}
              />
            </Box>

          </VStack>
        </Box>
      </Flex>

      <SaktiRemoteSessionLogModal
        isOpen={isLogModalOpen}
        onClose={onLogModalClose}
        sessionId={activeLogSessionId}
        logs={sessionLogs}
        loading={sessionLogsLoading}
      />
    </Box>
  );
};

export default SaktiRemoteInspectionPage;
